'use client';

import { getSession } from 'next-auth/react';

import { setBrowserApolloAccessTokenOverride } from '@/lib/apollo/client';

const PENDING_KEY_PREFIX = 'sportsfi_sensitive_action_pending_v1:';
const POPUP_MESSAGE_TYPE = 'sportsfi_sensitive_action_confirmation_v1';
const POPUP_NAME = 'sportsfi-sensitive-action-confirmation';
const POPUP_FEATURES = 'popup,width=520,height=760,menubar=no,toolbar=no,location=no,status=no';
const POPUP_TIMEOUT_MS = 2 * 60 * 1000;
const POPUP_POLL_MS = 500;
const POPUP_CLOSED_GRACE_MS = 1500;

export type SensitiveActionConfirmationLevel = 'password' | 'mfa' | 'admin_mfa' | 'passkey_mfa';

export type SensitiveActionConfirmationRequest = {
  actionId: string;
  targetResourceType?: string | null;
  targetResourceId?: string | null;
  operationKey: string;
  metadata?: Record<string, unknown> | null;
  reason?: string | null;
  requiredLevel?: SensitiveActionConfirmationLevel | null;
};

export type SensitiveActionConfirmationRetryHint = Omit<SensitiveActionConfirmationRequest, 'operationKey'>;

export type SensitiveActionConfirmationResult = SensitiveActionConfirmationRequest & {
  requiredLevel: SensitiveActionConfirmationLevel;
  token: string;
  expiresAt: string;
};

type PendingSensitiveActionConfirmation = {
  state: string;
  request: SensitiveActionConfirmationRequest;
  createdAt: number;
};

type SensitiveActionPopupMessage =
  | {
      type: typeof POPUP_MESSAGE_TYPE;
      state: string;
      status: 'confirmed';
      result: SensitiveActionConfirmationResult;
    }
  | {
      type: typeof POPUP_MESSAGE_TYPE;
      state: string;
      status: 'cancelled' | 'error';
      message?: string;
    };

export class SensitiveActionConfirmationCancelledError extends Error {
  constructor() {
    super('Security confirmation was cancelled.');
    this.name = 'SensitiveActionConfirmationCancelledError';
  }
}

export class SensitiveActionConfirmationBlockedError extends Error {
  constructor(public readonly reason: string | null = null) {
    super(reason ?? 'Security confirmation is not available for this sensitive action.');
    this.name = 'SensitiveActionConfirmationBlockedError';
  }
}

export const isSensitiveActionConfirmationCancelledError = (
  error: unknown
): error is SensitiveActionConfirmationCancelledError =>
  error instanceof SensitiveActionConfirmationCancelledError ||
  (error instanceof Error && error.name === 'SensitiveActionConfirmationCancelledError');

export const isSensitiveActionConfirmationBlockedError = (
  error: unknown
): error is SensitiveActionConfirmationBlockedError =>
  error instanceof SensitiveActionConfirmationBlockedError ||
  (error instanceof Error && error.name === 'SensitiveActionConfirmationBlockedError');

export type SensitiveActionConfirmationRequesterResult =
  | {
      status: 'confirmed';
      result: SensitiveActionConfirmationResult;
    }
  | {
      status: 'cancelled';
    }
  | {
      status: 'fallback';
    };

export type SensitiveActionConfirmationRequester = (
  request: SensitiveActionConfirmationRequest
) => Promise<SensitiveActionConfirmationRequesterResult>;

let sensitiveActionConfirmationRequester: SensitiveActionConfirmationRequester | null = null;

export const setSensitiveActionConfirmationRequester = (
  requester: SensitiveActionConfirmationRequester | null
): (() => void) => {
  sensitiveActionConfirmationRequester = requester;

  return () => {
    if (sensitiveActionConfirmationRequester === requester) {
      sensitiveActionConfirmationRequester = null;
    }
  };
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value != null && typeof value === 'object' && Array.isArray(value) !== true;

const randomState = (): string => {
  const bytes = new Uint8Array(24);

  crypto.getRandomValues(bytes);

  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
};

const getPendingKey = (state: string): string => `${PENDING_KEY_PREFIX}${state}`;

export const savePendingSensitiveActionConfirmation = (pending: PendingSensitiveActionConfirmation): void => {
  localStorage.setItem(getPendingKey(pending.state), JSON.stringify(pending));
};

export const consumePendingSensitiveActionConfirmation = (
  state: string | null | undefined
): SensitiveActionConfirmationRequest | null => {
  if (state == null || state.length === 0) {
    return null;
  }

  const key = getPendingKey(state);
  const raw = localStorage.getItem(key);

  localStorage.removeItem(key);

  if (raw == null) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as unknown;

    if (isRecord(parsed) !== true || parsed.state !== state || isRecord(parsed.request) !== true) {
      return null;
    }

    return normalizeSensitiveActionConfirmationRequest(parsed.request);
  } catch {
    return null;
  }
};

export const normalizeSensitiveActionConfirmationRequest = (
  value: unknown
): SensitiveActionConfirmationRequest | null => {
  if (
    isRecord(value) !== true ||
    typeof value.actionId !== 'string' ||
    value.actionId.trim().length === 0 ||
    typeof value.operationKey !== 'string' ||
    value.operationKey.trim().length === 0
  ) {
    return null;
  }

  const request: SensitiveActionConfirmationRequest = {
    actionId: value.actionId.trim(),
    operationKey: value.operationKey.trim(),
  };

  if (typeof value.targetResourceType === 'string' && value.targetResourceType.trim().length > 0) {
    request.targetResourceType = value.targetResourceType.trim();
  } else if (value.targetResourceType === null) {
    request.targetResourceType = null;
  }

  if (typeof value.targetResourceId === 'string' && value.targetResourceId.trim().length > 0) {
    request.targetResourceId = value.targetResourceId.trim();
  } else if (value.targetResourceId === null) {
    request.targetResourceId = null;
  }

  if (isRecord(value.metadata)) {
    request.metadata = value.metadata;
  }

  if (typeof value.reason === 'string' && value.reason.trim().length > 0) {
    request.reason = value.reason.trim();
  }

  if (
    value.requiredLevel === 'password' ||
    value.requiredLevel === 'mfa' ||
    value.requiredLevel === 'admin_mfa' ||
    value.requiredLevel === 'passkey_mfa'
  ) {
    request.requiredLevel = value.requiredLevel;
  }

  return request;
};

const normalizeSensitiveActionConfirmationResult = (value: unknown): SensitiveActionConfirmationResult | null => {
  const request = normalizeSensitiveActionConfirmationRequest(value);

  if (request == null || isRecord(value) !== true) {
    return null;
  }

  if (
    value.requiredLevel !== 'password' &&
    value.requiredLevel !== 'mfa' &&
    value.requiredLevel !== 'admin_mfa' &&
    value.requiredLevel !== 'passkey_mfa'
  ) {
    return null;
  }

  if (typeof value.token !== 'string' || value.token.trim().length === 0) {
    return null;
  }

  if (typeof value.expiresAt !== 'string' || value.expiresAt.trim().length === 0) {
    return null;
  }

  const expiresAtMs = Date.parse(value.expiresAt);

  if (Number.isFinite(expiresAtMs) !== true || expiresAtMs <= Date.now()) {
    return null;
  }

  return {
    ...request,
    expiresAt: value.expiresAt,
    requiredLevel: value.requiredLevel,
    token: value.token.trim(),
  };
};

const isSameConfirmationRequest = (
  left: SensitiveActionConfirmationRequest,
  right: SensitiveActionConfirmationRequest
): boolean =>
  left.actionId === right.actionId &&
  left.operationKey === right.operationKey &&
  (left.targetResourceType ?? null) === (right.targetResourceType ?? null) &&
  (left.targetResourceId ?? null) === (right.targetResourceId ?? null);

const normalizePopupMessage = (value: unknown): SensitiveActionPopupMessage | null => {
  if (isRecord(value) !== true || value.type !== POPUP_MESSAGE_TYPE || typeof value.state !== 'string') {
    return null;
  }

  if (value.status === 'cancelled') {
    return { type: POPUP_MESSAGE_TYPE, state: value.state, status: 'cancelled' };
  }

  if (value.status === 'error') {
    return {
      type: POPUP_MESSAGE_TYPE,
      state: value.state,
      status: 'error',
      message: typeof value.message === 'string' ? value.message : 'Unable to complete security confirmation.',
    };
  }

  if (value.status !== 'confirmed') {
    return null;
  }

  const result = normalizeSensitiveActionConfirmationResult(value.result);

  if (result == null) {
    return null;
  }

  return { type: POPUP_MESSAGE_TYPE, state: value.state, status: 'confirmed', result };
};

const getCsrfToken = async (): Promise<string> => {
  const response = await fetch('/api/auth/csrf');

  if (!response.ok) {
    throw new Error('Unable to start security confirmation: CSRF token could not be loaded.');
  }

  const data = (await response.json()) as { csrfToken?: string };

  if (typeof data.csrfToken !== 'string' || data.csrfToken.length === 0) {
    throw new Error('Unable to start security confirmation: CSRF token is missing.');
  }

  return data.csrfToken;
};

const getKeycloakSignInUrl = async (callbackUrl: string): Promise<string> => {
  const csrfToken = await getCsrfToken();
  const response = await fetch('/api/auth/signin/keycloak?prompt=login&max_age=0', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      callbackUrl,
      csrfToken,
      json: 'true',
    }),
  });

  if (!response.ok) {
    throw new Error('Unable to start security confirmation: sign-in request was rejected.');
  }

  const data = (await response.json()) as { url?: string };

  if (typeof data.url !== 'string' || data.url.length === 0) {
    throw new Error('Unable to start security confirmation: sign-in URL is missing.');
  }

  return data.url;
};

const refreshBrowserSessionToken = async (): Promise<void> => {
  const session = await getSession();

  setBrowserApolloAccessTokenOverride(session?.accessToken);
};

export const beginSensitiveActionConfirmationPopup = async (
  request: SensitiveActionConfirmationRequest
): Promise<SensitiveActionConfirmationResult | null> => {
  const state = randomState();

  savePendingSensitiveActionConfirmation({
    state,
    request,
    createdAt: Date.now(),
  });

  const callbackUrl = new URL('/auth/sensitive-action/complete', window.location.origin);

  callbackUrl.searchParams.set('state', state);

  const signInUrl = await getKeycloakSignInUrl(callbackUrl.toString());
  const popup = window.open(signInUrl, POPUP_NAME, POPUP_FEATURES);

  if (popup == null) {
    localStorage.removeItem(getPendingKey(state));
    throw new Error('Unable to open the security confirmation window.');
  }

  popup.focus();

  return new Promise((resolve, reject) => {
    let isSettled = false;
    let popupClosedAtMs: number | null = null;

    const cleanup = (): void => {
      window.removeEventListener('message', handleMessage);
      window.clearInterval(pollId);
      window.clearTimeout(timeoutId);
      localStorage.removeItem(getPendingKey(state));
    };

    const settle = (result: SensitiveActionConfirmationResult | null): void => {
      if (isSettled) {
        return;
      }

      isSettled = true;
      cleanup();
      resolve(result);
    };

    const fail = (error: Error): void => {
      if (isSettled) {
        return;
      }

      isSettled = true;
      cleanup();
      reject(error);
    };

    async function handleMessage(event: MessageEvent<unknown>): Promise<void> {
      if (event.origin !== window.location.origin) {
        return;
      }

      const message = normalizePopupMessage(event.data);

      if (message == null || message.state !== state) {
        return;
      }

      if (message.status === 'cancelled') {
        settle(null);

        return;
      }

      if (message.status === 'error') {
        fail(new Error(message.message));

        return;
      }

      if (message.status !== 'confirmed') {
        return;
      }

      if (isSameConfirmationRequest(request, message.result) !== true) {
        fail(new Error('Security confirmation did not match the requested action.'));

        return;
      }

      await refreshBrowserSessionToken();
      settle(message.result);
    }

    const pollId = window.setInterval(() => {
      if (popup.closed !== true) {
        popupClosedAtMs = null;

        return;
      }

      if (popupClosedAtMs == null) {
        popupClosedAtMs = Date.now();

        return;
      }

      if (Date.now() - popupClosedAtMs >= POPUP_CLOSED_GRACE_MS) {
        settle(null);
      }
    }, POPUP_POLL_MS);

    const timeoutId = window.setTimeout(() => {
      fail(new Error('Security confirmation timed out.'));
    }, POPUP_TIMEOUT_MS);

    window.addEventListener('message', handleMessage);
  });
};

export const requestSensitiveActionConfirmation = async (
  request: SensitiveActionConfirmationRequest
): Promise<SensitiveActionConfirmationResult | null> => {
  if (sensitiveActionConfirmationRequester != null) {
    const requesterResult = await sensitiveActionConfirmationRequester(request);

    if (requesterResult.status === 'confirmed') {
      return requesterResult.result;
    }

    if (requesterResult.status === 'cancelled') {
      return null;
    }
  }

  return beginSensitiveActionConfirmationPopup(request);
};

export const postSensitiveActionConfirmationMessage = (message: SensitiveActionPopupMessage): boolean => {
  if (window.opener == null) {
    return false;
  }

  window.opener.postMessage(message, window.location.origin);

  return true;
};

export const buildSensitiveActionConfirmedMessage = (
  state: string,
  result: SensitiveActionConfirmationResult
): SensitiveActionPopupMessage => ({
  result,
  state,
  status: 'confirmed',
  type: POPUP_MESSAGE_TYPE,
});

export const buildSensitiveActionErrorMessage = (state: string, message: string): SensitiveActionPopupMessage => ({
  message,
  state,
  status: 'error',
  type: POPUP_MESSAGE_TYPE,
});

const getGraphQLErrors = (error: unknown): Record<string, unknown>[] => {
  if (isRecord(error) !== true) {
    return [];
  }

  const candidates = [
    error.graphQLErrors,
    error.errors,
    isRecord(error.cause) ? error.cause.graphQLErrors : null,
    isRecord(error.cause) ? error.cause.errors : null,
    isRecord(error.networkError) && isRecord(error.networkError.result) ? error.networkError.result.errors : null,
  ];

  return candidates.flatMap((candidate) => (Array.isArray(candidate) ? candidate.filter(isRecord) : []));
};

const getGraphQLErrorExtensions = (error: unknown): Record<string, unknown> | null => {
  const graphQLError = getGraphQLErrors(error)[0];
  const extensions = graphQLError?.extensions;

  if (isRecord(extensions) !== true) {
    return null;
  }

  return extensions;
};

export const isSensitiveActionConfirmationRequiredError = (error: unknown, expectedActionId?: string): boolean => {
  const extensions = getGraphQLErrorExtensions(error);

  if (extensions?.code !== 'SENSITIVE_ACTION_CONFIRMATION_REQUIRED') {
    return false;
  }

  if (expectedActionId == null) {
    return true;
  }

  return extensions.actionId === expectedActionId;
};

export const getSensitiveActionConfirmationBlockedReason = (
  error: unknown,
  expectedActionId?: string
): string | null => {
  const extensions = getGraphQLErrorExtensions(error);

  if (extensions?.code !== 'SENSITIVE_ACTION_BLOCKED') {
    return null;
  }

  if (expectedActionId != null && extensions.actionId !== expectedActionId) {
    return null;
  }

  return typeof extensions.reason === 'string' && extensions.reason.length > 0
    ? extensions.reason
    : 'Security confirmation is not available for this sensitive action.';
};

const getSensitiveActionRequestFromError = (
  error: unknown,
  fallback: SensitiveActionConfirmationRetryHint
): SensitiveActionConfirmationRequest | null => {
  const extensions = getGraphQLErrorExtensions(error);

  if (extensions == null) {
    return normalizeSensitiveActionConfirmationRequest(fallback);
  }

  return normalizeSensitiveActionConfirmationRequest({
    ...fallback,
    ...(typeof extensions.actionId === 'string' && extensions.actionId.length > 0
      ? { actionId: extensions.actionId }
      : {}),
    ...(typeof extensions.targetResourceType === 'string' ? { targetResourceType: extensions.targetResourceType } : {}),
    ...(typeof extensions.targetResourceId === 'string' ? { targetResourceId: extensions.targetResourceId } : {}),
    ...(typeof extensions.operationKey === 'string' && extensions.operationKey.length > 0
      ? { operationKey: extensions.operationKey }
      : {}),
    ...(typeof extensions.reason === 'string' ? { reason: extensions.reason } : {}),
    ...(extensions.requiredLevel === 'password' ||
    extensions.requiredLevel === 'mfa' ||
    extensions.requiredLevel === 'admin_mfa' ||
    extensions.requiredLevel === 'passkey_mfa'
      ? { requiredLevel: extensions.requiredLevel }
      : {}),
  });
};

export const runWithSensitiveActionConfirmationRetry = async <T>({
  confirmation,
  run,
}: {
  confirmation: SensitiveActionConfirmationRetryHint;
  run: (sensitiveActionConfirmationToken: string | null) => Promise<T>;
}): Promise<T> => {
  let confirmationRequest: SensitiveActionConfirmationRequest | null = null;

  try {
    const result = await run(null);

    const blockedReason = getSensitiveActionConfirmationBlockedReason(result, confirmation.actionId);

    if (blockedReason != null) {
      throw new SensitiveActionConfirmationBlockedError(blockedReason);
    }

    if (isSensitiveActionConfirmationRequiredError(result, confirmation.actionId) !== true) {
      return result;
    }

    confirmationRequest = getSensitiveActionRequestFromError(result, confirmation);
  } catch (error) {
    const blockedReason = getSensitiveActionConfirmationBlockedReason(error, confirmation.actionId);

    if (blockedReason != null) {
      throw new SensitiveActionConfirmationBlockedError(blockedReason);
    }

    if (isSensitiveActionConfirmationRequiredError(error, confirmation.actionId) !== true) {
      throw error;
    }

    confirmationRequest = getSensitiveActionRequestFromError(error, confirmation);
  }

  if (confirmationRequest == null) {
    throw new Error('Unable to start security confirmation: operation key is missing.');
  }

  const result = await requestSensitiveActionConfirmation(confirmationRequest);

  if (result == null) {
    throw new SensitiveActionConfirmationCancelledError();
  }

  return run(result.token);
};
